import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePageContent } from "@/context/PageContentContext";
import Seo from "@/components/site/Seo";
import { orgJsonLd, getBaseUrl } from "@/seo";
import { Img } from "@/assets";
import StaticWorldMap from "@/components/site/StaticWorldMap";
import { contact } from "@/data/contact";

const VIDEO_SRC =
  "https://customer-assets-jt897jd0.emergentagent.net/job_bff87467-1ee2-45f5-a4b3-4ae91999047c/artifacts/1jpxspz4_Gold%20Petroleum%20Commercial%20v1%20%283%29.mp4";
const POSTER = "/commercial-poster.jpg";
const VIDEO_LABEL = "Gold Petroleum — Company Introduction";

export default function Home() {
  const c = usePageContent();
  const videoRef = useRef(null);
  const [needsUserStart, setNeedsUserStart] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setNeedsUserStart(true); return; }
    (async () => {
      try { v.muted = true; await v.play(); }
      catch { setNeedsUserStart(true); }
    })();
  }, []);

  const start = async () => {
    const v = videoRef.current; if (!v) return;
    try { v.muted = true; await v.play(); setNeedsUserStart(false); } catch {}
  };

  const toggleSound = async () => {
    const v = videoRef.current; if (!v) return;
    const next = !muted; v.muted = next; setMuted(next);
    if (!next) { try { await v.play(); } catch {} }
  };

  return (
    <>
      <Seo jsonLd={orgJsonLd(getBaseUrl())} />

      {/* HERO — split: copy left, commercial right */}
      <section className="gp-on-navy" data-testid="home-hero">
        <div className="gp-wrap gp-hero-split">
          <div className="gp-hero-split__grid">
            <div className="gp-hero-split__copy gp-enter">
              <div className="gp-eyebrow"><span className="gp-eyebrow-sep" /> Gold Petroleum FZ LLC</div>
              <h1 className="gp-h1" style={{ color: "var(--gp-white)", maxWidth: "16ch" }}>
                Energy. Metals.<br />
                <em style={{ fontStyle: "normal", color: "var(--gp-gold)" }}>Enduring partnerships.</em>
              </h1>
              <p className="gp-hero__sub">
                Gold Petroleum FZ LLC is a UAE-registered company focused on petroleum products
                and precious metals, with relationships across shipping, UAE storage and
                international refining.
              </p>
              <div className="gp-hero__ctas">
                {(() => {
                  const cp = contact.company_phone;
                  return (
                    <>
                      <a href={`tel:${cp.tel}`} className="gp-btn gp-btn--primary-call" data-testid="hero-cta-call">
                        Call our team <span className="gp-arrow">→</span>
                      </a>
                      <Link to="/contact" className="gp-btn" data-testid="hero-cta-enquiry">
                        Send a business enquiry <span className="gp-arrow">→</span>
                      </Link>
                      <Link to="/divisions" className="gp-link" data-testid="hero-cta-divisions">
                        Explore our divisions <span className="gp-arrow">→</span>
                      </Link>
                      <div className="gp-hero__call-line" data-testid="hero-call-primary">
                        {cp.raw}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="gp-hero-split__video gp-enter gp-enter-2" data-testid="commercial-frame" aria-label={VIDEO_LABEL}>
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                poster={POSTER}
                title={VIDEO_LABEL}
                aria-label={VIDEO_LABEL}
                controls controlsList="nodownload"
                playsInline muted loop preload="metadata"
                data-testid="commercial-video"
              />
              {needsUserStart && (
                <button type="button" className="gp-commercial__play" onClick={start}
                  aria-label={`Play ${VIDEO_LABEL}`} data-testid="commercial-play">
                  <span className="gp-commercial__play-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                </button>
              )}
              {!needsUserStart && muted && (
                <button type="button" className="gp-commercial__sound" onClick={toggleSound} data-testid="commercial-sound">
                  Tap for sound
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* RELATIONSHIPS BEHIND THE BUSINESS — 4 columns, compact orienting section */}
      <section className="gp-section gp-on-navy-2" data-testid="home-relationships-behind">
        <div className="gp-wrap">
          <div className="gp-eyebrow"><span className="gp-eyebrow-num">·</span><span className="gp-eyebrow-sep" /> Orientation</div>
          <h2 className="gp-h3" style={{ color: "var(--gp-white)", maxWidth: "26ch", marginBottom: 40 }}>
            Relationships behind the business.
          </h2>
          <div className="gp-rbb-grid" data-testid="rbb-grid">
            <div className="gp-rbb">
              <div className="gp-rbb__num">01</div>
              <div className="gp-rbb__title">Direct leadership contact</div>
              <p>Visitors can reach Ken Salvadore, President &amp; CEO, using the numbers above.</p>
            </div>
            <div className="gp-rbb">
              <div className="gp-rbb__num">02</div>
              <div className="gp-rbb__title">Maritime relationships</div>
              <p>A relationship with a Greece-based shipping company operating tankers and dry-cargo vessels.</p>
            </div>
            <div className="gp-rbb">
              <div className="gp-rbb__num">03</div>
              <div className="gp-rbb__title">UAE storage relationships</div>
              <p>Connections with terminal and storage-tank owners in Sharjah and Fujairah.</p>
            </div>
            <div className="gp-rbb">
              <div className="gp-rbb__num">04</div>
              <div className="gp-rbb__title">Refinery relationships</div>
              <p>Connections with international oil refineries.</p>
            </div>
          </div>
          <p className="gp-rbb__foot" data-testid="rbb-foot">
            Documented relationships, not owned infrastructure or guaranteed access.
          </p>
        </div>
      </section>

      {/* DIVISION PANELS — large photographic */}
      <section className="gp-section gp-on-white" data-testid="home-divisions">
        <div className="gp-wrap">
          <div className="gp-eyebrow" style={{ color: "var(--gp-ink)" }}>
            <span className="gp-eyebrow-num">01 </span><span className="gp-eyebrow-sep" /> Our divisions
          </div>
          <h2 className="gp-h2" style={{ maxWidth: "22ch", marginBottom: 48 }}>
            Two divisions, one standard of practice.
          </h2>
          <div className="gp-panels">
            <Link to="/divisions/petroleum-energy" className="gp-panel" data-testid="panel-petroleum">
              <Img id="refinery-dusk-wide" />
              <span className="gp-panel__shade" aria-hidden="true" />
              <span className="gp-panel__body">
                <span className="gp-panel__eyebrow">01 / Petroleum &amp; Energy</span>
                <span className="gp-panel__title">Petroleum, gas and refined products.</span>
                <p>Crude, refined products, gas and related commodities across international markets.</p>
                <span className="gp-panel__cta">See the division <span className="gp-arrow">→</span></span>
              </span>
            </Link>
            <Link to="/divisions/precious-metals" className="gp-panel" data-testid="panel-metals">
              <Img id="gold-dore-industrial" />
              <span className="gp-panel__shade" aria-hidden="true" />
              <span className="gp-panel__body">
                <span className="gp-panel__eyebrow">02 / Precious Metals</span>
                <span className="gp-panel__title">Raw, non-manufactured precious metals.</span>
                <p>Gold doré, silver and platinum — traded to industrial and institutional counterparties.</p>
                <span className="gp-panel__cta">See the division <span className="gp-arrow">→</span></span>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* MAP section */}
      <section className="gp-section gp-on-navy" data-testid="home-map">
        <div className="gp-wrap">
          <div className="gp-eyebrow"><span className="gp-eyebrow-num">02 </span><span className="gp-eyebrow-sep" /> Regional focus</div>
          <h2 className="gp-h2" style={{ color: "var(--gp-white)", maxWidth: "20ch", marginBottom: 32 }}>
            Connecting markets.
          </h2>
          <StaticWorldMap />
        </div>
      </section>

      {/* COMPANY STRIP */}
      <section className="gp-section gp-on-white" data-testid="home-company">
        <div className="gp-wrap">
          <div className="gp-strip">
            <div>
              <div className="gp-eyebrow" style={{ color: "var(--gp-ink)" }}>
                <span className="gp-eyebrow-num">03 </span><span className="gp-eyebrow-sep" /> Our company
              </div>
              <h2 className="gp-h2" style={{ maxWidth: "22ch" }}>A UAE-registered trading company.</h2>
              <p className="gp-body" style={{ marginTop: 20, maxWidth: "48ch" }}>
                {c["company.intro"]}
              </p>
              <div className="gp-strip__pillars">
                <div className="gp-strip__pillar"><strong>Transparency</strong><span>Clear terms, clear communication.</span></div>
                <div className="gp-strip__pillar"><strong>Consistency</strong><span>Reliable execution across cycles.</span></div>
                <div className="gp-strip__pillar"><strong>Efficiency</strong><span>Lean processes and disciplined logistics.</span></div>
                <div className="gp-strip__pillar"><strong>Shared value</strong><span>Outcomes designed for every stakeholder.</span></div>
              </div>
              <div style={{ marginTop: 32 }}>
                <Link to="/company" className="gp-link">More about the company <span className="gp-arrow">→</span></Link>
              </div>
            </div>
            <div className="gp-strip__media">
              <Img id="port-aerial-dawn" />
            </div>
          </div>
        </div>
      </section>

      {/* STRATEGIC RELATIONSHIPS TILES */}
      <section className="gp-section gp-on-navy-2" data-testid="home-relationships">
        <div className="gp-wrap">
          <div className="gp-eyebrow"><span className="gp-eyebrow-num">04 </span><span className="gp-eyebrow-sep" /> Strategic relationships</div>
          <h2 className="gp-h2" style={{ color: "var(--gp-white)", maxWidth: "24ch", marginBottom: 48 }}>
            Shipping, storage and refining — held together by relationships.
          </h2>
          <div className="gp-tiles">
            <Link to="/strategic-relationships" className="gp-rel-tile" data-testid="rel-shipping">
              <Img id="oil-tanker-sea" />
              <span className="gp-rel-tile__shade" aria-hidden="true" />
              <span className="gp-rel-tile__body">
                <span className="gp-rel-tile__label">01 · Shipping</span>
                <span className="gp-rel-tile__name">Greece-based fleet operator</span>
              </span>
            </Link>
            <Link to="/strategic-relationships" className="gp-rel-tile" data-testid="rel-storage">
              <Img id="storage-tanks-coastal" />
              <span className="gp-rel-tile__shade" aria-hidden="true" />
              <span className="gp-rel-tile__body">
                <span className="gp-rel-tile__label">02 · Storage</span>
                <span className="gp-rel-tile__name">Sharjah &amp; Fujairah terminals</span>
              </span>
            </Link>
            <Link to="/strategic-relationships" className="gp-rel-tile" data-testid="rel-refining">
              <Img id="refinery-pipes-close" />
              <span className="gp-rel-tile__shade" aria-hidden="true" />
              <span className="gp-rel-tile__body">
                <span className="gp-rel-tile__label">03 · Refining</span>
                <span className="gp-rel-tile__name">International refineries</span>
              </span>
            </Link>
          </div>
          <div style={{ marginTop: 40 }}>
            <Link to="/strategic-relationships" className="gp-link">See our regional focus <span className="gp-arrow">→</span></Link>
          </div>
        </div>
      </section>

      {/* LEADERSHIP PREVIEW — restrained editorial, no brick, no portrait */}
      <section className="gp-section gp-on-navy" data-testid="home-leadership">
        <div className="gp-wrap gp-narrow" style={{ textAlign: "center" }}>
          <div className="gp-eyebrow" style={{ justifyContent: "center" }}>
            <span className="gp-eyebrow-num">05 </span><span className="gp-eyebrow-sep" /> Leadership
          </div>
          <h2 className="gp-h2" style={{ color: "var(--gp-white)", margin: "0 auto 8px" }}>Ken Salvadore</h2>
          <div className="gp-lead__role" style={{ marginBottom: 20 }}>President &amp; CEO</div>
          <hr className="gp-rule-gold" style={{ margin: "0 auto 24px" }} />
          <p className="gp-lead__msg" data-testid="home-leadership-quote" style={{ color: "rgba(255,255,255,0.86)", fontStyle: "italic", fontWeight: 500, maxWidth: "48ch", margin: "0 auto" }}>
            “{c["leadership.short_quote"]}”
          </p>
          <div style={{ marginTop: 24 }}>
            <Link to="/leadership" className="gp-link" data-testid="home-leadership-cta">
              Meet the leadership <span className="gp-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT invitation */}
      <section className="gp-section gp-on-navy-2" data-testid="home-contact-teaser">
        <div className="gp-wrap">
          <div className="gp-eyebrow"><span className="gp-eyebrow-num">06 </span><span className="gp-eyebrow-sep" /> Get in touch</div>
          <h2 className="gp-h2" style={{ color: "var(--gp-white)", maxWidth: "22ch", marginBottom: 40 }}>
            Open a conversation with the team.
          </h2>
          <div className="gp-contact-grid">
            <div className="gp-contact-tile" data-testid="home-contact-tile-phone">
              <div className="gp-contact-tile__label">Telephone</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>Company telephone</div>
              <div className="gp-contact-tile__role">All hours enquiries</div>
              <div className="gp-contact-tile__lines">
                <a href={`tel:${contact.company_phone.tel}`} data-testid="home-contact-tel">
                  {contact.company_phone.raw}
                </a>
              </div>
            </div>
            <div className="gp-contact-tile" data-testid="home-contact-tile-emails">
              <div className="gp-contact-tile__label">Email</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>Written enquiries</div>
              <div className="gp-contact-tile__role">Sales &amp; general mailboxes</div>
              <div className="gp-contact-tile__lines">
                <a href={`mailto:${contact.sales_email.address}`} data-testid="home-contact-sales">
                  Sales · {contact.sales_email.address}
                </a>
                <a href={`mailto:${contact.general_email.address}`} data-testid="home-contact-general">
                  General · {contact.general_email.address}
                </a>
              </div>
            </div>
            <div className="gp-contact-tile" data-testid="home-contact-tile-leadership">
              <div className="gp-contact-tile__label">Leadership</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>{contact.leadership.name}</div>
              <div className="gp-contact-tile__role">{contact.leadership.title}</div>
              <div className="gp-contact-tile__lines">
                <span style={{ color: "rgba(255,255,255,0.6)" }}>Reach via company telephone or the sales mailbox above.</span>
              </div>
            </div>
            <div className="gp-contact-tile" data-testid="home-contact-tile-fabio">
              <div className="gp-contact-tile__label">Contact</div>
              <div className="gp-contact-tile__name" style={{ color: "var(--gp-white)" }}>Contact {contact.sales_contact.name}</div>
              <div className="gp-contact-tile__role">{contact.sales_contact.role}</div>
              <div className="gp-contact-tile__lines">
                <a href={`tel:${contact.sales_contact.tel}`} data-testid="home-contact-fabio-ca">
                  {contact.sales_contact.region}: {contact.sales_contact.raw}
                </a>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 48 }}>
            <Link to="/contact" className="gp-btn">Send an enquiry <span className="gp-arrow">→</span></Link>
          </div>
        </div>
      </section>
    </>
  );
}
